import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { trainingsService, Training, CreateTrainingData } from '../../services/trainings.service';
import categoriesService, { Category, Subcategory } from '../../services/categories.service';
import fieldsService, { Field } from '../../services/fields.service';
import countriesService, { Country, Wilaya } from '../../services/countries.service';
import { levelsService } from '../../services/levels.service';
import type { Level } from '../../services/levels.service';

const EditTrainingPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [training, setTraining] = useState<Training | null>(null);
  const [formData, setFormData] = useState<CreateTrainingData>({
    title: '',
    banner_image: '',
    country_id: undefined,
    city: '',
    reference: '',
    domaine_id: 0,
    subcategory_id: '',
    format: '',
    level: '',
    duration: '',
    schedule_date: '',
    full_description: '',
    learning_objectives: '',
    skills_gained: '',
    target_audience: '',
    detailed_program_pdf: '',
    trainer_profile_snippets: '',
    schedule_dates: '',
    pricing_dzd: 0,
    trainer: '',
    language: ''
  });
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null); // form submission errors
  const [loadError, setLoadError] = useState<string | null>(null); // initial load errors
  const [durationValue, setDurationValue] = useState<number>(0);
  const [durationUnit, setDurationUnit] = useState<string>('heures');
  
  // Hierarchical selection states
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [availableFields, setAvailableFields] = useState<Field[]>([]);
  const [levels, setLevels] = useState<Level[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [wilayas, setWilayas] = useState<Wilaya[]>([]);
  
  // Loading states
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  const [loadingFields, setLoadingFields] = useState(false);
  const [loadingLevels, setLoadingLevels] = useState(true);
  const [loadingCountries, setLoadingCountries] = useState(true);

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  // Load all necessary data
  const loadData = async () => {
    try {
      setLoadError(null);
      setIsLoading(true);
      
      // Load all data in parallel
      const [trainingData, categoriesData, levelsData, countriesData] = await Promise.all([
        trainingsService.getTraining(id!),
        categoriesService.getCategories(),
        levelsService.getAllLevels(),
        countriesService.getCountries()
      ]);
      
      setTraining(trainingData);
      setCategories(categoriesData);
      setLevels(levelsData);
      setCountries(countriesData);
      
      // Parse duration if it exists
      let durationVal = 0;
      let durationUnitVal = 'heures';
      if (trainingData.duration) {
        const durationMatch = trainingData.duration.match(/^(\d+)\s+(heures?|jours?|semaines?|mois)$/);
        if (durationMatch) {
          durationVal = parseInt(durationMatch[1]);
          durationUnitVal = durationMatch[2];
        }
      }
      
      setDurationValue(durationVal);
      setDurationUnit(durationUnitVal);
      
      // Set form data
      setFormData({
        title: trainingData.title || '',
        banner_image: trainingData.banner_image || '',
        country_id: trainingData.country?.id || undefined,
        city: trainingData.city || '',
        reference: trainingData.reference || '',
        domaine_id: trainingData.domaine_id || 0,
        subcategory_id: trainingData.subcategory_id || '',
        format: trainingData.format || '',
        level: trainingData.level || '',
        duration: trainingData.duration || '',
        schedule_date: trainingData.schedule_date || '',
        full_description: trainingData.full_description || '',
        learning_objectives: trainingData.learning_objectives || '',
        skills_gained: trainingData.skills_gained || '',
        target_audience: trainingData.target_audience || '',
        detailed_program_pdf: trainingData.detailed_program_pdf || '',
        trainer_profile_snippets: trainingData.trainer_profile_snippets || '',
        schedule_dates: trainingData.schedule_dates || '',
        pricing_dzd: trainingData.pricing_dzd || 0,
        trainer: trainingData.trainer || '',
        language: trainingData.language || ''
      });
      
      // Load subcategories and fields if subcategory is selected
      if (trainingData.subcategory_id) {
        // First get all subcategories to find the category
        const allSubcategories = await categoriesService.getSubcategories();
        const subcategory = allSubcategories.find((sub: Subcategory) => sub.id === trainingData.subcategory_id);
        
        if (subcategory) {
          // Load subcategories for this category
          await loadSubcategories(subcategory.category_id);
          // Load fields for this subcategory
          await loadFields(trainingData.subcategory_id);
        }
      }
      
      // Load wilayas if Algeria is selected
      if (trainingData.country?.name === 'Algérie' || trainingData.country?.name === 'Algeria') {
        await loadWilayas();
      }
      
    } catch (error) {
      setLoadError(t('training.edit.loadingError'));
    } finally {
      setIsLoading(false);
      setLoadingCategories(false);
      setLoadingLevels(false);
      setLoadingCountries(false);
    }
  };

  // Load subcategories when category changes
  const loadSubcategories = async (categoryId: string) => {
    try {
      setLoadingSubcategories(true);
      const subcategoriesData = await categoriesService.getSubcategoriesByCategory(categoryId);
      setSubcategories(subcategoriesData);
    } catch (error) {
    } finally {
      setLoadingSubcategories(false);
    }
  };

  // Load fields when subcategory changes
  const loadFields = async (subcategoryId: string) => {
    try {
      setLoadingFields(true);
      const fieldsData = await fieldsService.getFieldsBySubcategory(subcategoryId);
      setAvailableFields(fieldsData);
    } catch (error) {
    } finally {
      setLoadingFields(false);
    }
  };

  // Load wilayas for Algeria
  const loadWilayas = async () => {
    try {
      const wilayasData = await countriesService.getWilayas();
      setWilayas(wilayasData);
    } catch (error) {
    }
  };

  // Handle country change and load wilayas if Algeria is selected
  const handleCountryChange = async (countryId: number) => {
    
    // Find the country name to check if it's Algeria
    const selectedCountry = countries.find(c => c.id === countryId);
    const countryName = selectedCountry?.name || '';
    
    
    setFormData(prev => ({
      ...prev,
      country_id: countryId,
      city: '' // Reset city when country changes
    }));

    // Check for Algeria with exact matching (same as CreateTrainingPage)
    const isAlgeria = countryName === 'Algeria' || countryName === 'Algérie';
    

    if (isAlgeria) {
      try {
        const wilayasData = await countriesService.getWilayas();
        setWilayas(wilayasData);
      } catch (error) {
      }
    } else {
      setWilayas([]);
    }
  };


  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Handle number inputs properly
    let processedValue: any = value;
    if (name === 'domaine_id') {
      // Convert to number, use null if empty or invalid
      const numValue = parseInt(value);
      processedValue = isNaN(numValue) ? 0 : numValue;
    } else if (name === 'pricing_dzd') {
      // Convert to number, use 0 if empty or invalid
      const numValue = parseFloat(value);
      processedValue = isNaN(numValue) ? 0 : numValue;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));

    // Handle hierarchical selections
    if (name === 'subcategory_id') {
      // Clear fields when subcategory changes
      setAvailableFields([]);
      setFormData(prev => ({ ...prev, domaine_id: 0 }));
      
      // Load fields for the selected subcategory
      if (value) {
        loadFields(value);
      }
    }
  };

  const handleDurationChange = (value: number, unit: string) => {
    setDurationValue(value);
    setDurationUnit(unit);
    const durationString = value > 0 ? `${value} ${unit}` : '';
    setFormData(prev => ({
      ...prev,
      duration: durationString
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, fileType: 'banner' | 'pdf') => {
    const file = e.target.files?.[0];
    if (file) {
      if (fileType === 'banner') {
        setBannerFile(file);
        setFormData(prev => ({ ...prev, banner_image: file.name }));
      } else {
        setPdfFile(file);
        setFormData(prev => ({ ...prev, detailed_program_pdf: file.name }));
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    setIsSaving(true);
    try {
      const files = {
        banner: bannerFile || undefined,
        pdf: pdfFile || undefined,
      };
      await trainingsService.updateTraining(id, formData, files);
      alert(t('training.edit.updateSuccess'));
      navigate(`/training-center/trainings/${id}`);
    } catch (error: any) {
      let friendly = t('training.edit.updateError');

      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const apiMessage = (error.response?.data as any)?.message || (error.response?.data as any)?.error;
        if (status === 413) {
          friendly = t('training.edit.fileTooLarge') || 'Le fichier est trop volumineux (413).';
        } else if (apiMessage) {
          friendly = apiMessage;
        }
      } else if (error instanceof Error && error.message) {
        friendly = error.message;
      }

      setError(friendly);
    } finally {
      setIsSaving(false);
    }
  };


  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (loadError || !training) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('training.edit.notFound')}</h2>
          <p className="text-gray-600 mb-4">{loadError || t('training.edit.notFoundDescription')}</p>
          <button
            onClick={() => navigate('/training-center/trainings')}
            className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
          >
            {t('training.edit.backToTrainings')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('training.edit.title')}</h1>
              <p className="text-gray-600">{t('training.edit.subtitle')}</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => navigate(`/training-center/trainings/${training.id}`)}
                className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                  {t('training.edit.formTitle')} *
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="reference" className="block text-sm font-medium text-gray-700">
                  {t('training.edit.reference')}
                </label>
                <input
                  type="text"
                  id="reference"
                  name="reference"
                  value={formData.reference}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="country_id" className="block text-sm font-medium text-gray-700">
                  {t('training.edit.country')}
                </label>
                <select
                  id="country_id"
                  name="country_id"
                  value={formData.country_id || ''}
                  onChange={(e) => handleCountryChange(parseInt(e.target.value))}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                  <option value="">{t('training.edit.selectCountry')}</option>
                  {countries.map(country => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                  {(() => {
                    const selectedCountry = countries.find(c => c.id === formData.country_id);
                    return selectedCountry?.name === 'Algeria' || selectedCountry?.name === 'Algérie' ? t('training.edit.wilaya') : t('training.edit.city');
                  })()}
                </label>
                {(() => {
                  const selectedCountry = countries.find(c => c.id === formData.country_id);
                  const isAlgeria = selectedCountry?.name === 'Algeria' || selectedCountry?.name === 'Algérie';
                  
                  
                  if (isAlgeria) {
                    return (
                      <select
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      >
                        <option value="">{t('training.edit.selectWilaya')}</option>
                        {wilayas.map(wilaya => (
                          <option key={wilaya.id} value={wilaya.name}>
                            {wilaya.name}
                          </option>
                        ))}
                      </select>
                    );
                  } else {
                    return (
                      <input
                        id="city"
                        name="city"
                        type="text"
                        value={formData.city}
                        onChange={handleInputChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    );
                  }
                })()}
              </div>

              <div>
                <label htmlFor="subcategory_id" className="block text-sm font-medium text-gray-700">
                  {t('training.edit.subcategory')} *
                </label>
                <select
                  id="subcategory_id"
                  name="subcategory_id"
                  value={formData.subcategory_id || ''}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                  <option value="">{t('training.edit.selectSubcategory')}</option>
                  {subcategories.map(subcategory => (
                    <option key={subcategory.id} value={subcategory.id}>{subcategory.name_french}</option>
                  ))}
                </select>
                {loadingSubcategories && (
                  <p className="mt-1 text-sm text-gray-500">{t('training.edit.loadingSubcategories')}</p>
                )}
              </div>

              <div>
                <label htmlFor="domaine_id" className="block text-sm font-medium text-gray-700">
                  {t('training.edit.domain')} *
                </label>
                <select
                  id="domaine_id"
                  name="domaine_id"
                  value={formData.domaine_id || ''}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                  <option value="">{t('training.edit.selectDomain')}</option>
                  {availableFields.map(field => (
                    <option key={field.id} value={field.id}>{field.name_french}</option>
                  ))}
                </select>
                {loadingFields && (
                  <p className="mt-1 text-sm text-gray-500">{t('training.edit.loadingDomains')}</p>
                )}
              </div>

              <div>
                <label htmlFor="format" className="block text-sm font-medium text-gray-700">
                  {t('training.edit.format')}
                </label>
                <select
                  id="format"
                  name="format"
                  value={formData.format}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                  <option value="">{t('training.edit.selectFormat')}</option>
                  <option value="En présentiel">{t('training.edit.inPerson')}</option>
                  <option value="En ligne">{t('training.edit.online')}</option>
                  <option value="Hybride">{t('training.edit.hybrid')}</option>
                </select>
              </div>

              <div>
                <label htmlFor="level" className="block text-sm font-medium text-gray-700">
                  {t('training.edit.level')}
                </label>
                <select
                  id="level"
                  name="level"
                  value={formData.level}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                  <option value="">{t('training.edit.selectLevel')}</option>
                  {levels.map(level => (
                    <option key={level.id} value={level.name_french}>{level.name_french}</option>
                  ))}
                </select>
                {loadingLevels && (
                  <p className="mt-1 text-sm text-gray-500">{t('training.edit.loadingLevels')}</p>
                )}
              </div>

              <div>
                <label htmlFor="duration" className="block text-sm font-medium text-gray-700">
                  {t('training.edit.duration')}
                </label>
                <div className="mt-1 flex gap-2">
                  <input
                    id="duration"
                    name="duration"
                    type="number"
                    min="0"
                    value={durationValue}
                    onChange={(e) => handleDurationChange(Number(e.target.value), durationUnit)}
                    placeholder="0"
                    className="block w-24 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                  <select
                    value={durationUnit}
                    onChange={(e) => handleDurationChange(durationValue, e.target.value)}
                    className="block px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  >
                    <option value="heures">{t('training.edit.hours')}</option>
                    <option value="jours">{t('training.edit.days')}</option>
                    <option value="semaines">{t('training.edit.weeks')}</option>
                    <option value="mois">{t('training.edit.months')}</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="schedule_date" className="block text-sm font-medium text-gray-700">
                  {t('training.edit.scheduleDate')}
                </label>
                <input
                  type="date"
                  id="schedule_date"
                  name="schedule_date"
                  value={formData.schedule_date}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="pricing_dzd" className="block text-sm font-medium text-gray-700">
                  {t('training.edit.price')}
                </label>
                <input
                  type="number"
                  id="pricing_dzd"
                  name="pricing_dzd"
                  value={formData.pricing_dzd || 0}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="trainer" className="block text-sm font-medium text-gray-700">
                  {t('training.edit.trainer')}
                </label>
                <input
                  type="text"
                  id="trainer"
                  name="trainer"
                  value={formData.trainer}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="language" className="block text-sm font-medium text-gray-700">
                  {t('training.edit.language')}
                </label>
                <select
                  id="language"
                  name="language"
                  value={formData.language}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                  <option value="">{t('training.edit.selectLanguage')}</option>
                  <option value="Arabe">{t('training.edit.arabic')}</option>
                  <option value="Anglais">{t('training.edit.english')}</option>
                  <option value="Français">{t('training.edit.french')}</option>
                  <option value="Arabe/Anglais">{t('training.edit.arabicEnglish')}</option>
                  <option value="Arabe/Francais">{t('common.languages.arabicFrench')}</option>
                  <option value="Anglais/Francais">{t('common.languages.englishFrench')}</option>
                </select>
              </div>
            </div>

            {/* File Uploads */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="banner_file" className="block text-sm font-medium text-gray-700">
                  {t('training.edit.bannerImage')}
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                  <div className="space-y-1 text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                      aria-hidden="true"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="banner_file"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500"
                      >
                        <span>{t('training.edit.uploadFile')}</span>
                        <input
                          id="banner_file"
                          name="banner_file"
                          type="file"
                          accept="image/*"
                          className="sr-only"
                          onChange={(e) => handleFileChange(e, 'banner')}
                        />
                      </label>
                      <p className="pl-1">{t('training.edit.dragDrop')}</p>
                    </div>
                    <p className="text-xs text-gray-500">{t('training.edit.imageFormat')}</p>
                    {training.banner_image && (
                      <p className="text-sm text-gray-500">{t('training.edit.current')}: {training.banner_image}</p>
                    )}
                    {bannerFile && (
                      <p className="text-sm text-green-600">{t('training.edit.new')}: {bannerFile.name}</p>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="pdf_file" className="block text-sm font-medium text-gray-700">
                  {t('training.edit.detailedProgramPDF')}
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                  <div className="space-y-1 text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                      aria-hidden="true"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="pdf_file"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500"
                      >
                        <span>{t('training.edit.uploadFile')}</span>
                        <input
                          id="pdf_file"
                          name="pdf_file"
                          type="file"
                          accept=".pdf"
                          className="sr-only"
                          onChange={(e) => handleFileChange(e, 'pdf')}
                        />
                      </label>
                      <p className="pl-1">{t('training.edit.dragDrop')}</p>
                    </div>
                    <p className="text-xs text-gray-500">{t('training.edit.pdfFormat')}</p>
                    {training.detailed_program_pdf && (
                      <p className="text-sm text-gray-500">{t('training.edit.current')}: {training.detailed_program_pdf}</p>
                    )}
                    {pdfFile && (
                      <p className="text-sm text-green-600">{t('training.edit.new')}: {pdfFile.name}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Text Areas */}
            <div className="space-y-6">
              <div>
                <label htmlFor="full_description" className="block text-sm font-medium text-gray-700">
                  {t('training.edit.fullDescription')}
                </label>
                <textarea
                  id="full_description"
                  name="full_description"
                  rows={4}
                  value={formData.full_description}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="learning_objectives" className="block text-sm font-medium text-gray-700">
                  {t('training.edit.learningObjectives')}
                </label>
                <textarea
                  id="learning_objectives"
                  name="learning_objectives"
                  rows={4}
                  value={formData.learning_objectives}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="skills_gained" className="block text-sm font-medium text-gray-700">
                  {t('training.edit.skillsGained')}
                </label>
                <textarea
                  id="skills_gained"
                  name="skills_gained"
                  rows={4}
                  value={formData.skills_gained}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="target_audience" className="block text-sm font-medium text-gray-700">
                  {t('training.edit.targetAudience')}
                </label>
                <textarea
                  id="target_audience"
                  name="target_audience"
                  rows={4}
                  value={formData.target_audience}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="trainer_profile_snippets" className="block text-sm font-medium text-gray-700">
                  {t('training.edit.trainerProfile')}
                </label>
                <textarea
                  id="trainer_profile_snippets"
                  name="trainer_profile_snippets"
                  rows={4}
                  value={formData.trainer_profile_snippets}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>

            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end space-x-3 pt-6">
              <button
                type="button"
                onClick={() => navigate(`/training-center/trainings/${training.id}`)}
                className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {isSaving ? t('training.edit.saving') : t('training.edit.saveChanges')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditTrainingPage; 